import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ForceGraph2D from "react-force-graph-2d";
import { X, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PARTY_COLOR = { D: "#3b82f6", R: "#ef4444", I: "#a855f7" };
const PARTY_NAME = { D: "Democrat", R: "Republican", I: "Independent" };
const SCALE = 220;

export const IdeologyMap = ({ congress, chamber }) => {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const graphRef = useRef();
  const boxRef = useRef(null);
  const [dims, setDims] = useState({ w: 900, h: 640 });
  useEffect(() => {
    const measure = () => boxRef.current && setDims({ w: boxRef.current.clientWidth, h: Math.max(560, window.innerHeight - 320) });
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);

  useEffect(() => {
    setLoading(true);
    setSel(null);
    const params = { congress };
    if (chamber) params.chamber = chamber;
    axios.get(`${API}/network/ideology`, { params })
      .then((r) => { setPoints(r.data.points || []); setMeta(r.data.meta); })
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, [congress, chamber]);

  const graphData = useMemo(() => ({
    nodes: points.filter((p) => p.dim1 != null).map((p) => ({
      ...p, fx: p.dim1 * SCALE, fy: (p.dim2 || 0) * SCALE,
    })),
    links: [],
  }), [points]);

  const drawNode = useCallback((node, ctx, scale) => {
    ctx.globalAlpha = sel && sel.id !== node.id ? 0.35 : 1;
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.chamber === "Senate" ? 3.4 : 2.4, 0, 2 * Math.PI);
    ctx.fillStyle = PARTY_COLOR[node.party] || "#9ca3af";
    ctx.fill();
    if (sel && sel.id === node.id) {
      ctx.lineWidth = 1.5 / scale;
      ctx.strokeStyle = "#fff";
      ctx.stroke();
    }
    if (scale > 3.5) {
      ctx.font = `${9 / scale}px Inter, sans-serif`;
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.textAlign = "center";
      ctx.fillText(node.name.split(",")[0], node.x, node.y - 5 / scale);
    }
    ctx.globalAlpha = 1;
  }, [sel]);

  const tooltip = useCallback((n) =>
    `<div style="background:#141924;border:1px solid #2a3348;border-radius:8px;padding:8px 10px;font-size:12px;color:#e6e9f0;max-width:260px;">
      <b>${n.name}</b> <span style="color:${PARTY_COLOR[n.party] || "#aaa"}">(${n.party}-${n.state})</span><br/>
      <span style="color:#f97316">${n.read || ""}</span></div>`, []);

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-8">
        <div ref={boxRef} className="relative whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid="ideology-map">
          {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b0e14]/70"><Loader2 className="w-6 h-6 text-orange-400 animate-spin" /></div>}
          <ForceGraph2D
            ref={graphRef} width={dims.w} height={dims.h}
            graphData={graphData} backgroundColor="#0b0e14"
            nodeCanvasObject={drawNode} nodeLabel={tooltip}
            nodePointerAreaPaint={(node, color, ctx) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(node.x, node.y, 5, 0, 2 * Math.PI); ctx.fill(); }}
            onNodeClick={(n) => setSel(n)} onBackgroundClick={() => setSel(null)}
            cooldownTicks={0} enableNodeDrag={false}
            onEngineStop={() => graphRef.current && graphRef.current.zoomToFit(400, 50)}
          />
          <div className="absolute bottom-3 inset-x-0 text-center text-[11px] text-white/45 pointer-events-none">← More liberal&nbsp;&nbsp;·&nbsp;&nbsp;More conservative →</div>
          <div className="absolute top-3 left-3 bg-[#10141d]/90 border border-white/10 rounded-lg px-3 py-2 text-[11px] text-white/55 max-w-[260px]" data-testid="ideology-legend">
            DW-NOMINATE ideal points (Voteview). Horizontal = classic left, right scale. Vertical = second dimension, often read as establishment vs. outsider politics.
          </div>
        </div>
      </div>
      <div className="col-span-12 lg:col-span-4">
        {sel ? (
          <div className="whip-surface rounded-xl border border-orange-500/30 p-4" data-testid="ideology-panel">
            <div className="flex items-start gap-2">
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">{sel.name}</h3>
                <p className="text-xs mt-0.5" style={{ color: PARTY_COLOR[sel.party] || "#aaa" }}>{PARTY_NAME[sel.party] || sel.party} · {sel.state} · {sel.chamber}</p>
              </div>
              <button onClick={() => setSel(null)} className="ml-auto text-white/40 hover:text-white" data-testid="ideology-panel-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-white/80 mt-3 leading-relaxed">{sel.read}</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                <p className="text-lg font-bold text-orange-300">{sel.dim1?.toFixed(2)}</p>
                <p className="text-[10px] text-white/50 uppercase tracking-wider">Left, right score</p>
              </div>
              <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                <p className="text-lg font-bold text-orange-300">{sel.dim2?.toFixed(2)}</p>
                <p className="text-[10px] text-white/50 uppercase tracking-wider">Second dimension</p>
              </div>
            </div>
            <p className="text-[10px] text-white/35 mt-2">Scores run −1 (most liberal) to +1 (most conservative), estimated from every roll-call vote they have cast.</p>
            <Link to={`/legislator/${sel.legislatorId}`} className="mt-4 inline-flex items-center gap-1 text-sm text-orange-300 hover:text-orange-200" data-testid="ideology-full-profile">
              Full profile <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="whip-surface rounded-xl border border-white/10 p-4 text-sm text-white/50" data-testid="ideology-hint">
            <p className="text-white/80 font-medium mb-2">How to read this map</p>
            <p className="leading-relaxed">Every dot is a member of Congress placed by their entire voting record. Dots far from their party's cluster are the mavericks; the gap between the two clouds is a direct picture of polarization.</p>
            {meta && <p className="mt-3 text-[11px] text-white/35">{meta.count} members · {meta.congress}th Congress</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeologyMap;
